/**
 * File-upload client for POST /api/chat/upload.
 *
 * Like chat-stream, this can't go through the vendored `api` JSON wrapper — the
 * endpoint takes multipart/form-data, not a JSON body. It still routes its
 * bearer token through the SAME resolver seam (`acquireToken`) so auth stays
 * single-sourced; at merge the token source swaps with the rest of the app by
 * changing only src/auth/.
 *
 * Contract (apps/experts/api/chat/upload.js):
 *   Request:  multipart/form-data, field `file`. The client must NOT send a
 *             `path` — the server mints the storage path from the authed user
 *             ID + a fresh UUID for tenant isolation.
 *   Response: 200 { url: "<1-hour read-only SAS URL>", path: "<storage path>" }
 *   Errors:   401 | 413 (>10 MB) | 415 (bad MIME) | 503 (storage down) | 500
 *
 * The endpoint returns the server-minted storage `path` alongside the SAS
 * `url`. POST /api/chat/send validates that each attachment's `path` starts
 * with `<userId>/`, so we send back the EXACT server-minted path here — it is
 * guaranteed to match where the bytes live (no fragile client-side recompute).
 * The `path` field is additive; the legacy chat.js still works reading only
 * `url`.
 */
import { acquireToken } from '@/auth/msal';
import { AuthTokenUnavailableError } from '@andavo/web-ui/api';
import type { ChatRequestConfig } from './chat-stream';
import { chatUrl, getChatScopes } from '@/features/chat/chat-config';

const UPLOAD_PATH = '/api/chat/upload';

export type UploadResult = { url: string; path: string };

/** HTTP-status-aware error so the composer can show a precise message. */
export class UploadError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
  }
}

/**
 * Upload a single file. Returns the SAS read URL and the server-minted storage
 * path on success; throws UploadError (with the HTTP status) or
 * AuthTokenUnavailableError otherwise.
 */
export async function uploadFile(
  file: File,
  signal?: AbortSignal,
  config: ChatRequestConfig = {},
): Promise<UploadResult> {
  // CHAT-audience token via the scope-parameterized acquireToken (same seam as
  // the SSE streamer); baseUrl injected (same-origin by default).
  const scopes = config.scopes ?? getChatScopes();
  const token = await acquireToken(scopes);
  if (!token) {
    // Mirror the api wrapper: never send an unauthenticated request.
    throw new AuthTokenUnavailableError();
  }

  const form = new FormData();
  form.append('file', file);

  const url = config.baseUrl
    ? `${config.baseUrl.replace(/\/$/, '')}/${UPLOAD_PATH.replace(/^\//, '')}`
    : chatUrl(UPLOAD_PATH);
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
    signal,
  });

  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new UploadError(message, res.status);
  }

  const data = (await res.json()) as Partial<UploadResult>;
  if (!data.url) {
    throw new UploadError('Upload succeeded but returned no URL', 500);
  }
  if (!data.path) {
    // The server must mint the storage path; without it /api/chat/send would
    // reject the attachment (path must start with `<userId>/`). Fail loudly
    // rather than silently recomputing a path that may not match the bytes.
    throw new UploadError('Upload succeeded but returned no storage path', 500);
  }
  return { url: data.url, path: data.path };
}
